import type { Span } from '@opentelemetry/api'
import type { SpanProcessor } from '@opentelemetry/sdk-trace-base'

import {
  ATTR_GECKO_USER_IS_INTERNAL,
  ATTR_USER_EMAIL,
  ATTR_USER_ID,
} from './spanAttributes'

/**
 * User context that can be updated dynamically after SDK initialization.
 */
export interface UserContext {
  userId: string
  userEmail: string
  isInternalUser: boolean
}

/**
 * A SpanProcessor that adds user context attributes to spans.
 * Unlike resource attributes (which are fixed at SDK init), this processor
 * reads from a mutable context store, allowing user info to be updated
 * after authentication without reinitializing the SDK.
 *
 * This is necessary because OpenTelemetry global APIs (trace, propagation, etc.)
 * can only be registered once - calling sdk.shutdown() and reinitializing
 * causes "duplicate registration" errors.
 */
export class UserContextSpanProcessor implements SpanProcessor {
  private userContext: UserContext | null = null

  /**
   * Update the user context. Called when user authentication state changes.
   */
  setUserContext(context: UserContext | null): void {
    this.userContext = context
  }

  onStart(span: Span): void {
    if (this.userContext) {
      span.setAttribute(ATTR_USER_ID, this.userContext.userId)
      span.setAttribute(ATTR_USER_EMAIL, this.userContext.userEmail)
      span.setAttribute(
        ATTR_GECKO_USER_IS_INTERNAL,
        this.userContext.isInternalUser
      )
    }
  }

  onEnd(): void {}

  forceFlush(): Promise<void> {
    return Promise.resolve()
  }

  shutdown(): Promise<void> {
    return Promise.resolve()
  }
}
